import { BetsCycleLoader } from '@/components/ui/betscycle-loader'
import { P2pUserAvatar } from '@/components/p2p/detail/P2pUserAvatar'
import { P2P_HOLDERS_LIMIT } from '@/config/p2p'
import { formatAmount } from '@/lib/format-number'
import { useGetP2pHoldersQuery } from '@/store/api/p2pApi'
import type { P2pHolderEntry } from '@/types/p2p'

interface P2pTopHoldersProps {
	marketId: string
	optionId: string
}

function HolderColumn({ title, sharesClassName, holders }: { title: string; sharesClassName: string; holders: P2pHolderEntry[] }) {
	if (holders.length === 0) {
		return (
			<div className='min-w-0 flex-1 rounded-xl border border-border bg-background p-4'>
				<div className='mb-3 flex items-center justify-between border-b border-border pb-3 text-sm font-medium tracking-[-0.21px] text-muted-foreground'>
					<span>{title}</span>
					<span>Shares</span>
				</div>
				<p className='py-4 text-center text-sm text-muted-foreground'>No holders yet.</p>
			</div>
		)
	}

	return (
		<div className='min-w-0 flex-1 rounded-xl border border-border bg-background p-4'>
			<div className='mb-3 flex items-center justify-between border-b border-border pb-3 text-sm font-medium tracking-[-0.21px] text-muted-foreground'>
				<span>{title}</span>
				<span>Shares</span>
			</div>

			<div className='space-y-3'>
				{holders.map((holder) => {
					const username = holder.username?.trim() || 'Anonymous'

					return (
						<div key={`${holder.option_type}-${holder.player_id}`} className='flex items-center justify-between gap-3'>
							<div className='flex min-w-0 items-center gap-2.5'>
								<P2pUserAvatar username={username} avatarUrl={holder.avatar_url} />
								<span className='truncate text-sm font-medium tracking-[-0.21px] text-foreground'>{username}</span>
							</div>
							<span className={sharesClassName}>{formatAmount(holder.shares)}</span>
						</div>
					)
				})}
			</div>
		</div>
	)
}

export function P2pTopHolders({ marketId, optionId }: P2pTopHoldersProps) {
	const { data, isLoading, isError } = useGetP2pHoldersQuery({
		market_id: marketId,
		option_id: optionId,
		limit: P2P_HOLDERS_LIMIT,
	})

	if (isLoading) {
		return (
			<div className='flex min-h-[160px] items-center justify-center'>
				<BetsCycleLoader size={32} aria-label='Loading holders' />
			</div>
		)
	}

	if (isError) {
		return <p className='py-4 text-center text-sm text-muted-foreground'>Unable to load holders right now.</p>
	}

	const yesHolders = data?.data.yes ?? []
	const noHolders = data?.data.no ?? []

	return (
		<div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
			<HolderColumn title='Yes holders' sharesClassName='text-sm font-semibold text-success' holders={yesHolders} />
			<HolderColumn title='No holders' sharesClassName='text-sm font-semibold text-destructive' holders={noHolders} />
		</div>
	)
}
